import { cn } from '../../lib/utils';
import { PopoverContent } from '../ui/popover';
import { inFormCurrency } from './catalog-suggestions';
import type { CatalogConversion } from './catalog-types';
import { panelCount, panelUnitMoney } from './panel-format';
import { saleFromMarkup } from './tier-pricing';

/** One catalogue price the admin can pick for a tier. */
export type CatalogOption = {
	quantity: number;
	baseUnitPrice: number;
	currency: string;
	markupPercentage?: number | null;
	minProductionDays?: number | null;
	maxProductionDays?: number | null;
	/** This price in the form's currency. Null when no conversion is known. */
	converted?: CatalogConversion | null;
	/** True on the break the catalogue itself applied to this tier. */
	matched: boolean;
	/** `EXACT`, `BRACKET` or `BELOW_MINIMUM` — only meaningful on the matched one. */
	match?: string;
	/** Whose price this is. Null is the default (no-supplier) price. */
	vetterEntityId?: string | null;
	vetterName?: string | null;
	/**
	 * Whether this tier could actually be priced from it. A supplier's price is
	 * only usable once that supplier is the one assigned — otherwise picking it
	 * would quote a figure they never gave for this offer.
	 */
	eligible: boolean;
};

/** 10%, 16,67% — the panel's Danish decimals, trailing zeros dropped. */
const percent = (value: number) =>
	`${new Intl.NumberFormat('da-DK', { maximumFractionDigits: 2 }).format(value)}%`;

const days = (min?: number | null, max?: number | null) =>
	min == null ? null : max == null || max === min ? `${min} days` : `${min}–${max} days`;

/** "10% markup · 10–14 days", the line under a price. */
const detail = (o: CatalogOption) =>
	[
		o.markupPercentage != null ? `${percent(o.markupPercentage)} markup` : 'No markup',
		days(o.minProductionDays, o.maxProductionDays),
	]
		.filter(Boolean)
		.join(' · ');

/** What the catalogue reached for, said on the best-match line. */
const matchLabel = (o: CatalogOption) =>
	o.match === 'EXACT'
		? 'exact quantity'
		: o.match === 'BELOW_MINIMUM'
			? `smallest price break, ${panelCount(o.quantity)} pcs`
			: `price break from ${panelCount(o.quantity)} pcs`;

type Group = { key: string; name: string; vetterEntityId: string | null; options: CatalogOption[] };

/** One group per price list, default first, then suppliers by name — like the catalogue page. */
function groupOptions(options: CatalogOption[]): Group[] {
	const byKey = new Map<string, Group>();
	for (const o of options) {
		const key = o.vetterEntityId ?? 'default';
		const group = byKey.get(key) ?? {
			key,
			name: o.vetterName ?? 'Default price',
			vetterEntityId: o.vetterEntityId ?? null,
			options: [],
		};
		group.options.push(o);
		byKey.set(key, group);
	}
	return [...byKey.values()]
		.map((g) => ({ ...g, options: [...g.options].sort((a, b) => a.quantity - b.quantity) }))
		.sort(
			(a, b) =>
				Number(a.vetterEntityId != null) - Number(b.vetterEntityId != null) ||
				a.name.localeCompare(b.name)
		);
}

/**
 * Every catalogue price for this product, as a list to choose from.
 *
 * The best match sits on top with its own Use button; below it the prices are
 * grouped by price list, as on the catalogue page. Each option states its own
 * volume, so picking the 1000 price for a 400 tier is a visible decision rather
 * than an accident.
 */
export function CatalogSourcePicker({
	quantity,
	options,
	selectedQuantity,
	selectedVetterEntityId,
	onPick,
	onManual,
	assignable = false,
	formCurrency,
}: {
	/** The tier being priced. */
	quantity: number;
	options: CatalogOption[];
	/**
	 * Which catalogue price is filling this tier — quantity AND supplier.
	 *
	 * Quantity alone does not identify a row: a product can carry a 500 price
	 * for nobody and a 500 price for a supplier.
	 */
	selectedQuantity?: number | null;
	selectedVetterEntityId?: string | null;
	onPick: (option: CatalogOption) => void;
	onManual: () => void;
	/** The currency this form saves in. A price in another is usable once converted. */
	formCurrency: string;
	/**
	 * Whether picking another supplier's price may reassign the offer to them.
	 * Otherwise such a row stays unusable and says why.
	 */
	assignable?: boolean;
}) {
	const usable = (o: CatalogOption) =>
		!!inFormCurrency(o, formCurrency) && (o.eligible || assignable);
	const best = options.find((o) => o.matched);
	const groups = groupOptions(options);

	/** "100,00 kr. → 110,00 kr.", in the form's currency. */
	const priceLine = (o: CatalogOption) => {
		const price = inFormCurrency(o, formCurrency);
		if (!price) return panelUnitMoney(o.baseUnitPrice, o.currency);
		const cost = panelUnitMoney(price.baseUnitPrice, formCurrency);
		const sale = saleFromMarkup(
			String(price.baseUnitPrice),
			o.markupPercentage != null ? String(o.markupPercentage) : ''
		);
		return sale ? `${cost} → ${panelUnitMoney(Number(sale), formCurrency)}` : cost;
	};

	/** What was really quoted, whenever a rate was applied. */
	const quotedLine = (o: CatalogOption) => {
		const quoted = inFormCurrency(o, formCurrency)?.quoted;
		return quoted ? `quoted ${panelUnitMoney(quoted.baseUnitPrice, quoted.currency)}` : null;
	};

	const groupHint = (g: Group) => {
		if (g.vetterEntityId == null) return 'Used when the supplier has no price of its own';
		if (g.options[0]?.eligible) return 'Assigned to this offer';
		return assignable
			? `Picking one assigns ${g.name} to this offer`
			: `Only usable once ${g.name} is assigned`;
	};

	const whyNot = (o: CatalogOption) =>
		!inFormCurrency(o, formCurrency)
			? `Priced in ${o.currency}, with no ${formCurrency} figure for it.`
			: `Only for ${o.vetterName} — assign that supplier to use it.`;

	return (
		/*
		 * The app's popover, not an absolutely-positioned div: this opens inside a
		 * scrolling step, where an absolute child is clipped by the scroll box.
		 * Capped to the room the positioner reports; only the list scrolls, so the
		 * heading, the best match and "Enter manually" stay in reach.
		 */
		<PopoverContent
			align="start"
			side="bottom"
			sideOffset={6}
			className="flex max-h-[min(34rem,var(--available-height))] w-[26rem] flex-col gap-0 overflow-hidden p-0"
		>
			<div className="flex items-baseline justify-between gap-3 border-b border-line-1 px-3.5 py-2.5">
				<span className="text-sm font-medium text-text-1">Catalogue prices</span>
				<span className="text-xs text-text-3">
					for {panelCount(quantity)} pcs · in {formCurrency}
				</span>
			</div>

			{best && (
				<div className="flex items-center gap-3 border-b border-line-1 bg-accent-soft px-3.5 py-3">
					<div className="flex min-w-0 flex-1 flex-col gap-0.5">
						<span
							className={cn(
								'text-2xs font-medium uppercase tracking-[0.05em]',
								best.match === 'BELOW_MINIMUM' ? 'text-status-warn' : 'text-accent'
							)}
						>
							Best match · {matchLabel(best)}
						</span>
						<span className="font-mono text-sm font-medium text-text-1">
							{priceLine(best)}
						</span>
						<span className="text-xs text-text-3">
							{[
								best.vetterName ?? 'Default price',
								`${panelCount(best.quantity)} pcs`,
								detail(best),
								quotedLine(best),
							]
								.filter(Boolean)
								.join(' · ')}
						</span>
						{/* Reaching UP the ladder is the one that can lose money: a
						    shorter run costs more per unit, so the figure is a floor. */}
						{best.match === 'BELOW_MINIMUM' && (
							<span className="text-xs text-status-warn">
								A shorter run costs more per unit, so this is a floor on the cost.
							</span>
						)}
					</div>
					{usable(best) ? (
						<button
							type="button"
							onClick={() => onPick(best)}
							className="h-7 shrink-0 rounded-md bg-accent px-3 text-xs font-medium text-accent-fg transition-colors hover:bg-accent-hover"
						>
							Use
						</button>
					) : (
						<span className="max-w-28 text-right text-2xs text-text-3">
							{whyNot(best)}
						</span>
					)}
				</div>
			)}

			<div className="scroll-thin min-h-0 flex-1 overflow-y-auto">
				{groups.map((g) => (
					<div key={g.key}>
						<div className="flex flex-col gap-0.5 border-b border-line-1 bg-surface-fill px-3.5 pb-1.5 pt-2.5">
							<span className="text-xs font-medium text-text-1">{g.name}</span>
							<span className="text-2xs text-text-3">{groupHint(g)}</span>
						</div>
						{g.options.map((o) => {
							const ok = usable(o);
							const selected =
								o.quantity === selectedQuantity &&
								(o.vetterEntityId ?? null) === (selectedVetterEntityId ?? null);
							return (
								<button
									key={`${g.key}-${o.quantity}`}
									type="button"
									disabled={!ok}
									onClick={() => ok && onPick(o)}
									title={!ok ? whyNot(o) : undefined}
									className={cn(
										'grid w-full grid-cols-[64px_minmax(0,1fr)] items-center gap-2.5 border-b border-line-1 px-3.5 py-2 text-left transition-colors last:border-0',
										selected
											? 'bg-accent-soft'
											: o.matched && 'bg-surface-fill',
										ok
											? 'hover:bg-surface-hover'
											: 'cursor-not-allowed opacity-45'
									)}
								>
									<span className="font-mono text-sm text-text-2">
										{panelCount(o.quantity)}{' '}
										<span className="font-sans text-xs text-text-3">pcs</span>
									</span>
									<span className="flex min-w-0 flex-col gap-0.5">
										<span className="truncate font-mono text-sm font-medium text-text-1">
											{priceLine(o)}
										</span>
										<span className="truncate text-xs text-text-3">
											{[detail(o), quotedLine(o)].filter(Boolean).join(' · ')}
										</span>
									</span>
								</button>
							);
						})}
					</div>
				))}
			</div>

			<div className="flex justify-end border-t border-line-1 px-2.5 py-1.5">
				<button
					type="button"
					onClick={onManual}
					className="rounded-[var(--r-2)] px-2 py-1 text-xs text-text-2 transition-colors hover:bg-surface-hover hover:text-text-1"
				>
					Enter manually
				</button>
			</div>
		</PopoverContent>
	);
}
